import { useRef } from "react";

const LABELS = {
  professional: "Professional",
  personal: "Personal",
  all: "All",
};

function normalizeContexts(contexts) {
  const input = Array.isArray(contexts) ? contexts : [contexts];
  const expanded = input.flatMap((context) => context === "both"
    ? ["professional", "personal"]
    : [context]);
  return [...new Set(expanded)].filter((context) => LABELS[context]);
}

export function ContextBadge({
  context,
  compact = false,
  active = false,
  onSelect,
  buttonRef,
  tabIndex,
  onKeyDown,
  className = "",
}) {
  const normalized = context === "both" ? "all" : context;
  const label = LABELS[normalized];
  if (!label) return null;

  const sharedProps = {
    className: `context-badge context-${normalized}${compact ? " is-compact" : ""}${onSelect ? " is-interactive" : ""}${active ? " is-active" : ""} ${className}`.trim(),
    "aria-label": compact ? `${label} context` : undefined,
    title: compact ? label : undefined,
  };

  const contents = (
    <>
      <span className="context-badge-mark" aria-hidden="true" />
      {!compact && <span>{label}</span>}
    </>
  );

  if (onSelect) {
    return (
      <button
        {...sharedProps}
        type="button"
        role="tab"
        aria-selected={active}
        ref={buttonRef}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        onClick={() => onSelect(normalized)}
      >
        {contents}
      </button>
    );
  }

  return (
    <span {...sharedProps}>{contents}</span>
  );
}

/** Render the personal/professional markers attached to one deduplicated person. */
export default function ContextBadges({
  contexts = [],
  compact = false,
  activeContext,
  onSelect,
  className = "",
}) {
  const normalized = normalizeContexts(contexts);
  const tabsRef = useRef([]);
  if (normalized.length === 0) return null;

  const selected = normalized.includes(activeContext) ? activeContext : normalized[0];
  const handleKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (index + 1) % normalized.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (index - 1 + normalized.length) % normalized.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = normalized.length - 1;
    }
    if (nextIndex !== null) {
      event.preventDefault();
      onSelect(normalized[nextIndex]);
      tabsRef.current[nextIndex]?.focus();
    }
  };

  return (
    <span
      className={`context-badges${onSelect ? " is-interactive" : ""} ${className}`.trim()}
      role={onSelect ? "tablist" : undefined}
      aria-label={onSelect ? "Relationship context" : undefined}
    >
      {normalized.map((context, index) => (
        <ContextBadge
          key={context}
          context={context}
          compact={compact}
          active={selected === context}
          onSelect={onSelect}
          buttonRef={(node) => { tabsRef.current[index] = node; }}
          tabIndex={onSelect ? (selected === context ? 0 : -1) : undefined}
          onKeyDown={onSelect ? (event) => handleKeyDown(event, index) : undefined}
        />
      ))}
    </span>
  );
}
